/**
 * Profile Page
 * User settings and account management with editable budget distribution
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBudgetStore } from '../store/budgetStore';
import { MoonIcon, SunIcon, ProfileIcon } from '../components/icons/CategoryIcons';
import { useTheme, useUserPreferences, useUpdatePreferences } from '../hooks/useUserPreferences';
import { useExpenseEntries } from '../hooks/useExpenseEntries';
import { useRevenueEntries, useRevenueSummary } from '../hooks/useRevenueEntries';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { useUIStore } from '../store/uiStore';
import {
  DEFAULT_BUDGET_DISTRIBUTION,
  getInitialBudgetDistribution,
  type CategoryPercentage,
} from '../utils/budgetDistribution';
import { getCategoryName } from '../components/ui/CategoryLabel';
import { CurrencyModal } from '../components/ui/CurrencyModal';
import {
  type SupportedCurrency,
  CURRENCY_SYMBOLS,
  CURRENCY_NAMES,
  convertFromBRL,
} from '../types';
import { version as appVersion } from '../../package.json';

interface ProfilePageProps {
  onLogout: () => void;
  onNavigateToSettings: () => void;
}

// Format number for display (uses pt-BR for BRL, en-US for others)
const formatCurrencyValue = (value: number, currency: SupportedCurrency): string => {
  return value.toLocaleString(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export function ProfilePage({ onLogout, onNavigateToSettings }: ProfilePageProps) {
  const { t } = useTranslation();
  const { categories, updateAllCategoryPercentages } = useBudgetStore();
  const { isDark, toggleTheme, isUpdating: themeUpdating } = useTheme();
  const { preferences, refetch, isLoading: preferencesLoading } = useUserPreferences();
  const updatePreferences = useUpdatePreferences();
  const { theme } = useUIStore();
  const isLight = theme === 'light';
  const [showNotificationMessage, setShowNotificationMessage] = useState(false);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);

  // Fetch exchange rates from database (refreshed every 12 hours)
  const { rates: exchangeRates } = useExchangeRates();

  // Get current currency from preferences, default to BRL
  const currentCurrency = (preferences?.defaultCurrency as SupportedCurrency) || 'BRL';

  // Current month/year for data fetching
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Use expense entries - amounts in BRL converted using currencySnapshots
  const { total: expenseTotal, isLoading: expensesLoading } = useExpenseEntries(
    currentYear,
    currentMonth,
    currentCurrency
  );

  // Use revenue entries - amounts in BRL converted using currencySnapshots
  const { total: revenueTotal, isLoading: revenueEntriesLoading } = useRevenueEntries(
    currentYear,
    currentMonth,
    currentCurrency
  );

  // Handle notification toggle click - show coming soon message
  const handleNotificationClick = useCallback(() => {
    setShowNotificationMessage(true);
    setTimeout(() => setShowNotificationMessage(false), 3000);
  }, []);

  // Handle currency change
  const handleCurrencyChange = useCallback((currency: SupportedCurrency) => {
    updatePreferences.mutate(
      { defaultCurrency: currency },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  }, [updatePreferences, refetch]);

  // Revenue summary from RevenueEntry data for rolling average (backend calculates in BRL)
  // Note: For rolling average we still use backend summary as it covers multiple months
  const {
    rollingAverage: rollingAverageBRL,
    monthsWithData,
    isLoading: revenueSummaryLoading,
  } = useRevenueSummary();

  // Combined loading state for revenue data
  const revenueLoading = revenueEntriesLoading || revenueSummaryLoading;

  // Rolling average converted to user's preferred currency using database rates
  // Backend calculates this from individual entries with their stored rates (in BRL)
  const rollingAverage = convertFromBRL(rollingAverageBRL, currentCurrency, exchangeRates);

  // State for editable percentages
  const [editablePercentages, setEditablePercentages] = useState<CategoryPercentage[]>(DEFAULT_BUDGET_DISTRIBUTION);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize editable percentages from preferences or store, falling back to defaults
  useEffect(() => {
    // Wait for preferences to load if authenticated
    if (preferencesLoading) return;

    const preferencesCategories = preferences?.budgetConfig?.categories;
    const initialDistribution = getInitialBudgetDistribution(preferencesCategories, categories);

    setEditablePercentages(initialDistribution);
  }, [preferences, categories, preferencesLoading]);

  // Calculate total percentage
  const totalPercentage = useMemo(() => {
    return editablePercentages.reduce((sum, cat) => sum + cat.percentage, 0);
  }, [editablePercentages]);

  // Validation: percentages must sum to 100
  const isValid = totalPercentage === 100;

  // Handle percentage change for a category
  const handlePercentageChange = (id: string, value: string) => {
    const numValue = parseInt(value, 10) || 0;
    const clampedValue = Math.max(0, Math.min(100, numValue));

    setEditablePercentages((prev) =>
      prev.map((cat) =>
        cat.id === id ? { ...cat, percentage: clampedValue } : cat
      )
    );
    setHasChanges(true);
  };

  // Save changes to the server
  const handleSave = async () => {
    if (!isValid) return;

    // Update local store immediately
    updateAllCategoryPercentages(
      editablePercentages.map((cat) => ({ id: cat.id, percentage: cat.percentage }))
    );

    // Sync with server
    updatePreferences.mutate(
      {
        budgetConfig: {
          categories: editablePercentages.map((cat) => ({
            id: cat.id,
            nameKey: cat.nameKey,
            percentage: cat.percentage,
          })),
        },
      },
      {
        onSuccess: () => {
          setHasChanges(false);
          refetch(); // Refresh preferences from server
        },
      }
    );
  };

  // Cancel changes
  const handleCancel = () => {
    const preferencesCategories = preferences?.budgetConfig?.categories;
    const initialDistribution = getInitialBudgetDistribution(preferencesCategories, categories);
    setEditablePercentages(initialDistribution);
    setHasChanges(false);
  };

  // Total spent this month - already converted to target currency using stored rates
  const totalSpent = expenseTotal;

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Profile Header */}
      <div className="flex flex-col items-center text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 overflow-hidden ${
          isLight ? 'bg-slate-200' : 'bg-slate-800'
        }`}>
          {preferences?.avatarUrl ? (
            <img src={preferences.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <ProfileIcon className={`w-10 h-10 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} />
          )}
        </div>
        <h2 className={`text-xl font-semibold ${isLight ? 'text-slate-900' : 'text-slate-100'}`}>
          {preferences?.name || 'Usuário'}
        </h2>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
          {preferences?.email || 'usuario@email.com'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-xl p-4 text-center ${isLight ? 'bg-slate-100' : 'bg-slate-900/50'}`}>
          <p className={`text-xs mb-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('pages.profile.monthlyIncome')}
          </p>
          {revenueLoading ? (
            <p className={`font-bold text-lg font-mono ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
              ...
            </p>
          ) : (
            <p className={`font-bold text-lg font-mono ${isLight ? 'text-emerald-700' : 'text-emerald-300'}`} data-testid="monthly-budget-display">
              {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(revenueTotal, currentCurrency)}
            </p>
          )}
        </div>
        <div className={`rounded-xl p-4 text-center ${isLight ? 'bg-slate-100' : 'bg-slate-900/50'}`}>
          <p className={`text-xs mb-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{t('pages.profile.monthlySpent')}</p>
          {expensesLoading ? (
            <p className={`font-bold text-lg font-mono ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
              ...
            </p>
          ) : (
            <p className="text-red-400 font-bold text-lg font-mono" data-testid="monthly-spent-display">
              {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(totalSpent, currentCurrency)}
            </p>
          )}
        </div>
      </div>

      {/* Rolling Average Revenue Card */}
      <div className={`rounded-xl p-4 ${isLight ? 'bg-emerald-50 border border-emerald-200' : 'bg-emerald-900/20 border border-emerald-800'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-xs font-medium mb-1 ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`}>
              {t('pages.profile.rollingAverage')}
            </p>
            {revenueLoading ? (
              <p className={`font-bold text-xl font-mono ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                ...
              </p>
            ) : (
              <p className={`font-bold text-xl font-mono ${isLight ? 'text-emerald-700' : 'text-emerald-300'}`} data-testid="rolling-average-display">
                {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(rollingAverage, currentCurrency)}
              </p>
            )}
          </div>
          <div className={`${isLight ? 'text-emerald-300' : 'text-emerald-600/40'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
          </div>
        </div>
        {monthsWithData > 0 && (
          <div className={`mt-3 pt-3 border-t ${isLight ? 'border-emerald-200' : 'border-emerald-800/50'}`}>
            <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              {t(monthsWithData === 1 ? 'pages.profile.basedOn' : 'pages.profile.basedOn_plural', { count: monthsWithData })}
            </p>
          </div>
        )}
        {monthsWithData === 0 && !revenueLoading && (
          <div className={`mt-3 pt-3 border-t ${isLight ? 'border-emerald-200' : 'border-emerald-800/50'}`}>
            <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              {t('pages.profile.registerIncome')}
            </p>
          </div>
        )}
      </div>

      {/* Category Percentages - Editable */}
      <div className={`rounded-xl p-4 ${isLight ? 'bg-slate-100' : 'bg-slate-900/50'}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-semibold ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
            {t('pages.profile.budgetDistribution')}
          </h3>
          <div className={`text-xs font-medium px-2 py-1 rounded ${
            isValid
              ? 'bg-green-500/20 text-green-500'
              : 'bg-red-500/20 text-red-500'
          }`}>
            {t('pages.profile.totalPercentage', { value: totalPercentage })}
          </div>
        </div>

        <div className="space-y-3">
          {editablePercentages.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
                <span className={`text-sm truncate ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                  {getCategoryName(t, cat.nameKey, cat.name)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={cat.percentage}
                  onChange={(e) => handlePercentageChange(cat.id, e.target.value)}
                  className={`w-16 text-right text-sm font-medium px-2 py-1 rounded border outline-none transition-colors ${
                    isLight
                      ? 'bg-white border-slate-300 text-slate-800 focus:border-primary-500'
                      : 'bg-slate-800 border-slate-700 text-slate-200 focus:border-primary-500'
                  }`}
                />
                <span className={`text-sm ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>%</span>
              </div>
            </div>
          ))}
        </div>

        {/* Validation Warning */}
        {!isValid && (
          <div className="mt-3 text-xs text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{t('pages.profile.percentageWarning')}</span>
          </div>
        )}

        {/* Save/Cancel Buttons */}
        {hasChanges && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleCancel}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                isLight
                  ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid || updatePreferences.isPending}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                isValid
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              {updatePreferences.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className={`rounded-xl divide-y ${
        isLight ? 'bg-slate-100 divide-slate-200' : 'bg-slate-900/50 divide-slate-800'
      }`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {isDark ? (
              <MoonIcon className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} />
            ) : (
              <SunIcon className="w-5 h-5 text-yellow-500" />
            )}
            <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>
              {isDark ? t('pages.profile.darkMode') : t('pages.profile.lightMode')}
            </span>
          </div>
          <button
            onClick={toggleTheme}
            disabled={themeUpdating}
            className={`w-11 h-6 rounded-full transition-colors ${
              isDark ? 'bg-primary-500' : 'bg-slate-400'
            } ${themeUpdating ? 'opacity-50' : ''}`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                isDark ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Currency Selector */}
        <button
          onClick={() => setIsCurrencyModalOpen(true)}
          data-testid="currency-selector-button"
          className={`flex items-center justify-between p-4 w-full text-left transition-colors ${
            isLight ? 'hover:bg-slate-200/50' : 'hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{t('pages.profile.defaultCurrency', 'Moeda Padrão')}</span>
              <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                {t(`currencies.${currentCurrency}`, CURRENCY_NAMES[currentCurrency])}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isLight ? 'text-primary-600' : 'text-primary-400'}`}>
              {CURRENCY_SYMBOLS[currentCurrency]} {currentCurrency}
            </span>
            <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>

        <div className="flex items-center justify-between p-4 relative">
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{t('pages.profile.notifications')}</span>
          </div>
          <button
            onClick={handleNotificationClick}
            className={`w-11 h-6 rounded-full transition-colors ${isLight ? 'bg-slate-400' : 'bg-slate-700'}`}
          >
            <div className="w-5 h-5 rounded-full bg-white shadow transform transition-transform translate-x-0.5" />
          </button>
          {/* Coming soon message */}
          {showNotificationMessage && (
            <div className={`absolute right-0 top-full mt-2 px-3 py-2 rounded-lg text-sm font-medium shadow-lg z-10 animate-fade-in ${
              isLight ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-amber-900/90 text-amber-200 border border-amber-700'
            }`}>
              {t('pages.profile.notificationsComingSoon')}
            </div>
          )}
        </div>

        <button
          onClick={onNavigateToSettings}
          data-testid="profile-settings-button"
          className={`flex items-center justify-between gap-3 p-4 w-full text-left transition-colors ${
            isLight ? 'hover:bg-slate-200/50' : 'hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{t('pages.profile.settings')}</span>
          </div>
          <svg className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button className={`flex items-center gap-3 p-4 w-full text-left transition-colors ${
          isLight ? 'hover:bg-slate-200/50' : 'hover:bg-slate-800/50'
        }`}>
          <svg className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{t('pages.profile.help')}</span>
        </button>
      </div>

      {/* Logout Button */}
      <button
        onClick={onLogout}
        className="w-full flex items-center justify-center gap-2 py-3 border border-red-900 rounded-xl text-red-400 hover:bg-red-900/20 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        <span className="font-medium">{t('pages.profile.logout')}</span>
      </button>

      {/* Version */}
      <p className="text-center text-slate-600 text-xs">
        {t('pages.profile.version', { version: appVersion })}
      </p>

      {/* Currency Modal */}
      <CurrencyModal
        isOpen={isCurrencyModalOpen}
        selectedCurrency={currentCurrency}
        onSelect={handleCurrencyChange}
        onClose={() => setIsCurrencyModalOpen(false)}
      />
    </div>
  );
}
